/**
 * Tenant Info Service
 * Handles API requests related to tenant information
 */
import { createService } from './serviceFactory';

// Define the base URL for tenant info service
const baseUrl = '/api/supplier/tenant-info';

// Create base service
const baseService = createService(baseUrl);

const tenantInfoService = {
  /**
   * Get entity counts for the current tenant
   * @returns {Promise} - Promise that resolves with entity counts
   */
  getEntityCounts() {
    return baseService.client.get(`${baseUrl}/entity-counts`)
      .then(response => response.data)
      .catch(error => {
        console.error('Error fetching entity counts:', error);
        throw error;
      });
  },

  /**
   * Get tenant extra properties (email, phone, about, displayName, companyLogoId)
   * @returns {Promise} - Promise that resolves with extra properties
   */
  getExtraProperties() {
    return baseService.client.get(`${baseUrl}/extra-properties`)
      .then(response => response.data)
      .catch(error => {
        console.error('Error fetching tenant extra properties:', error);
        throw error;
      });
  },

  /**
   * Save tenant extra properties
   * @param {Object} extraProperties - Object containing email, phone, about, displayName, companyLogoId
   * @returns {Promise} - Promise that resolves with saved extra properties
   */
  saveExtraProperties(extraProperties) {
    return baseService.client.post(`${baseUrl}/save-extra-properties`, extraProperties)
      .then(response => response.data)
      .catch(error => {
        console.error('Error saving tenant extra properties:', error);
        throw error;
      });
  },

  /**
   * Get a specific extra property by key
   * @param {string} propertyKey - Property key (Email, Phone, About, DisplayName, CompanyLogoId)
   * @returns {Promise} - Promise that resolves with property value
   */
  getExtraProperty(propertyKey) {
    return baseService.client.get(`${baseUrl}/extra-property`, {
      params: { propertyKey }
    })
      .then(response => response.data)
      .catch(error => {
        console.error(`Error fetching tenant extra property ${propertyKey}:`, error);
        throw error;
      });
  },

  /**
   * Set a specific extra property
   * @param {string} propertyKey - Property key (Email, Phone, About, DisplayName, CompanyLogoId)
   * @param {string} propertyValue - Property value
   * @returns {Promise} - Promise that resolves with success
   */
  setExtraProperty(propertyKey, propertyValue) {
    return baseService.client.post(`${baseUrl}/set-extra-property`, {
      propertyKey,
      propertyValue
    })
      .then(response => response.data)
      .catch(error => {
        console.error(`Error setting tenant extra property ${propertyKey}:`, error);
        throw error;
      });
  },

  /**
   * Get tenant geo location
   * @returns {Promise} - Promise that resolves with geo location data
   */
  getGeoLocation() {
    return baseService.client.get(`${baseUrl}/geo-location`)
      .then(response => response.data)
      .catch(error => {
        console.error('Error fetching tenant geo location:', error);
        throw error;
      });
  },

  /**
   * Save tenant geo location
   * @param {Object} geoLocationData - Geo location data
   * @returns {Promise} - Promise that resolves with saved geo location data
   */
  saveGeoLocation(geoLocationData) {
    return baseService.client.post(`${baseUrl}/save-geo-location`, geoLocationData)
      .then(response => response.data)
      .catch(error => {
        console.error('Error saving tenant geo location:', error);
        throw error;
      });
  },

  /**
   * Delete tenant geo location
   * @returns {Promise} - Promise that resolves with success
   */
  deleteGeoLocation() {
    return baseService.client.delete(`${baseUrl}/geo-location`)
      .then(response => response.data)
      .catch(error => {
        console.error('Error deleting tenant geo location:', error);
        throw error;
      });
  },

  /**
   * Delete all tenant extra properties
   * @returns {Promise} - Promise that resolves with success
   */
  deleteExtraProperties() {
    return baseService.client.delete(`${baseUrl}/extra-properties`)
      .then(response => response.data)
      .catch(error => {
        console.error('Error deleting tenant extra properties:', error);
        throw error;
      });
  }
};

export default tenantInfoService; 